import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SectionFormBox from '../../../components/SectionFormBox/SectionFormBox';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import useCreateAvisoObra from './hooks/useCreateAvisoObra';
import { useLocation } from 'react-router-dom';
import DynamicData from '../../Presentaciones/components/DynamicData';
import Step1 from './components/Step1';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import StepNavigator from './components/StepNavigator';
import StepNavigatorFooter from './components/StepNavigatorFooter';
import { enqueueSnackbar } from 'notistack';
import { validateStep1 } from './validations/validateStep1';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import { useRef, useState } from 'react';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { validateStep2 } from './validations/validateStep2';

const CreateAvisoObra = () => {
    const { t } = useTranslation('avisoObra');
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { isEdit, isView, ...initialData } = location.state || {};

    const [step1Errors, setStep1Errors] = useState<Record<string, string>>({});
    const [step2Errors, setStep2Errors] = useState<Record<string, string>>({});

    const [isLoadingNext, setIsLoadingNext] = useState(false);
    const hasLoadedInitialData = useRef(false);

    const {
        idAvisoObra,
        step,
        setStep,
        formData,
        handleChange,
        handleCancel,
        isNextDisabled,
        loading,
        hasMultipleContracts,
        contractFields,
        loadingContracts,
        handleStep1Submit,
        tiposObra,
        handleFinalSubmit,
        handleSaveDraft,
        isSavingDraft
    } = useCreateAvisoObra({ isEdit, initialData });

    const avisoObraId = isEdit ? initialData.id : idAvisoObra;

    useEffect(() => {
        if (isEdit && initialData && !hasLoadedInitialData.current) {
            hasLoadedInitialData.current = true;
            const direccion = initialData.direccion ?? {};
            const localidad = direccion.localidad ?? {};
            const provincia = localidad.provincia ?? {};

            const mappedFormData = {
                calleRuta: direccion.calle ?? '',
                numeroKm: direccion.altura ?? '',
                piso: direccion.piso ?? '',
                departamento: direccion.departamento ?? '',
                latitud: direccion.latitud ?? '',
                longitud: direccion.longitud ?? '',
                idProvincia: provincia.id ?? null,
                provinciaNombre: provincia.nombre ?? '',
                idLocalidad: localidad.id ?? null,
                localidadNombre: localidad.nombre ?? '',
                codigoPostal: localidad.codigoPostal ?? '',
                superficieM2: initialData.superficieM2 ?? '',
                cantidadPlantas: initialData.cantidadPlantas ?? '',
                descripcionObra: initialData.descripcionObra ?? '',
                idTipoResolucion: initialData.idTipoResolucion ?? null,
                pdfProgramaUrl: initialData.programaSeguridad?.[0]?.urlArchivo ?? '',
                programaSeguridad:
                    initialData.programaSeguridad?.map((doc: any) => ({
                        idPrograma: doc.idPrograma,
                        urlArchivo: doc.urlArchivo,
                        fechaPresentacion: doc.fechaPresentacion
                            ? dayjs(doc.fechaPresentacion).format('YYYY-MM-DD')
                            : null
                    })) ?? [],
                fechaPresentacionPrograma: initialData.programaSeguridad?.[0]?.fechaPresentacion
                    ? dayjs(initialData.programaSeguridad[0].fechaPresentacion).format('YYYY-MM-DD')
                    : null,
                fechaInicio: initialData.fechaInicio
                    ? dayjs.utc(initialData.fechaInicio).format('YYYY-MM-DD')
                    : null,
                fechaFin: initialData.fechaFin
                    ? dayjs.utc(initialData.fechaFin).format('YYYY-MM-DD')
                    : null,
                avisosObraTiposSubtipos: initialData.avisosObraTiposSubtipos || [],
                avisosObraActividadesEspeciales: initialData.avisosObraActividadesEspeciales || [],
                descripcionActividad: initialData.descripcionActividad ?? '',
                responsables: (initialData.responsables || []).map((r: any) => ({
                    nombre: r.nombre ?? '',
                    apellido: r.apellido ?? '',
                    cuit: r.cuitCuilCuip ?? '',
                    cargoNombre: r.cargo?.nombre ?? '',
                    idCargo: r.cargo?.id ?? '',
                    representacionNombre: r.representacion?.nombre ?? '',
                    idRepresentacion: r.representacion?.id ?? '',
                    tipoContratacionNombre: r.tipoContratacion?.nombre ?? '',
                    idTipoContratacion: r.tipoContratacion?.id ?? '',
                    titulo: r.tituloHabilitante ?? '',
                    matricula: r.nroMatricula ?? '',
                    entidadOtorgante: r.entidadOtorgante ?? ''
                })),
                contratistas: (initialData.contratistas || []).map((c: any) => ({
                    idContratista: c.idContratista ?? null,
                    cuit: c.cuitCuilCuip ?? '',
                    razonSocial: c.razonSocial ?? ''
                }))
            };

            handleChange({ target: { name: 'idAvisoObra', value: initialData.id } } as any);
            handleChange({ target: { name: 'responsables', value: mappedFormData.responsables } });

            for (const [key, value] of Object.entries(mappedFormData)) {
                handleChange({ target: { name: key, value } } as any);
            }
        }
    }, [isEdit, initialData]);

    const handleNextStep1 = async () => {
        if (step !== 1) return;

        if (isView) {
            setStep((prev) => prev + 1);
            return;
        }

        const errors = validateStep1(formData);
        setStep1Errors(errors);

        if (Object.keys(errors).length > 0) {
            enqueueSnackbar('Hay errores en el formulario.', { variant: 'error' });
            return;
        }

        try {
            setIsLoadingNext(true);

            if (idAvisoObra != null || avisoObraId != null) {
                setStep((prev) => prev + 1);
            } else {
                await handleStep1Submit();
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo avanzar al paso 2 del aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setIsLoadingNext(false);
        }
    };

    const handleNextStep2 = async () => {
        if (step !== 2) return;

        const errors = validateStep2(formData);
        setStep2Errors(errors);

        if (Object.keys(errors).length > 0) {
            enqueueSnackbar('Hay errores en el formulario.', { variant: 'error' });
            return;
        }

        setStep((prev) => prev + 1);
    };

    const hasResponsableTecnico =
        step !== 3 ||
        (Array.isArray(formData.responsables) &&
            formData.responsables.some((r) => r.idCargo === 2));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '90vh' }}>
            <Box mb={3}>
                <SectionFormBox title={t('headerContract.subtitleAgency')}>
                    <DynamicData fields={contractFields} />
                </SectionFormBox>
            </Box>

            <StepNavigator
                step={step}
                steps={[
                    {
                        label: t('step1.title'),
                        component: (
                            <Step1
                                t={t}
                                formData={formData}
                                handleChange={handleChange}
                                errors={step1Errors}
                                setErrors={setStep1Errors}
                                isView={isView}
                                idAvisoObra={idAvisoObra}
                            />
                        )
                    },
                    {
                        label: t('step2.title'),
                        component: (
                            <Step2
                                t={t}
                                formData={formData}
                                handleChange={handleChange}
                                errors={step2Errors}
                                tiposObra={tiposObra}
                                isView={isView}
                            />
                        )
                    },
                    {
                        label: t('step3.title'),
                        component: (
                            <Step3
                                t={t}
                                formData={formData}
                                handleChange={handleChange}
                                isView={isView}
                            />
                        )
                    }
                ]}
            />

            <StepNavigatorFooter
                step={step}
                isView={isView}
                setStep={setStep}
                onCancel={handleCancel}
                onSubmit={step === 3 ? handleFinalSubmit : handleStep1Submit}
                idAvisoObra={avisoObraId}
                onSaveDraft={() => handleSaveDraft(formData, avisoObraId)}
                onNext={step === 1 ? handleNextStep1 : step === 2 ? handleNextStep2 : undefined}
                isNextDisabled={step === 3 ? !hasResponsableTecnico : isNextDisabled}
                formData={formData}
                isNextLoading={isLoadingNext}
                isSavingDraft={isSavingDraft}
            />

            <BackdropComponent
                loading={loading || loadingContracts || isSavingDraft || isLoadingNext}
            />
        </Box>
    );
};

export default CreateAvisoObra;
